import React, { useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  FaTooth,
  FaGem,
  FaTools,
  FaComments,
  FaClipboardCheck,
  FaSearch,
} from "react-icons/fa";

const Services = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const services = [
    {
      id: 1,
      icon: <FaTooth />,
      title: "Custom Grillz",
      shortDescription:
        "Premium custom-fitted grillz made with the finest materials.",
      description:
        "Our custom grillz are meticulously crafted to provide the perfect fit and exceptional quality. From solid gold to diamond-encrusted designs, we create statement pieces that reflect your unique style and personality.",
      features: [
        "Made with 10K, 14K, or 18K gold (yellow, white, or rose)",
        "Diamond options including VVS clarity stones",
        "Precise dental impressions for perfect fit",
        "Custom designs from single teeth to full sets",
        "Available in various styles: open-face, solid, two-tone, or iced-out",
      ],
      image: "/images/bg-pic.jpg",
    },
    {
      id: 2,
      icon: <FaGem />,
      title: "Custom Jewelry",
      shortDescription:
        "Bespoke jewelry pieces handcrafted to your specifications.",
      description:
        "Express your unique style with our custom jewelry creations. We work closely with you to design and craft stunning pieces that tell your story, from concept to completion.",
      features: [
        "Pendants, chains, bracelets, rings, and earrings",
        "Premium materials including gold, platinum, and silver",
        "Gemstone and diamond setting",
        "Custom engraving options",
        "Personalized design consultation",
      ],
      image: "/images/img2.jpg",
    },
    {
      id: 3,
      icon: <FaTools />,
      title: "Restoration & Repair",
      shortDescription:
        "Expert restoration and repair services for jewelry and grillz.",
      description:
        "Bring new life to your treasured pieces with our expert restoration and repair services. Our skilled technicians can fix damaged jewelry, resize rings, replace stones, and perform comprehensive repairs to restore your items to their original glory.",
      features: [
        "Chain and clasp repair",
        "Stone replacement and setting",
        "Ring sizing and reshaping",
        "Grillz adjustment and refinishing",
        "Metal refinishing and polishing",
      ],
      image: "/images/img3.jpg",
    },
    {
      id: 4,
      icon: <FaComments />,
      title: "Consultations",
      shortDescription: "Expert guidance for custom piece design and creation.",
      description:
        "Not sure what you want? Our consultation service provides expert guidance to help you conceptualize and design the perfect custom piece. Our experienced designers will work with you to understand your vision and bring it to life.",
      features: [
        "One-on-one design sessions",
        "Material selection guidance",
        "Budget planning assistance",
        "Visual mockups and 3D renderings",
        "Timeline development",
      ],
      image: "/images/img1.jpg",
    },
    {
      id: 5,
      icon: <FaClipboardCheck />,
      title: "Appraisals & Authentication",
      shortDescription:
        "Professional jewelry appraisal and authentication services.",
      description:
        "Our certified professionals provide accurate appraisals and authentication services for your valuable pieces, ensuring you have proper documentation for insurance, resale, or personal records.",
      features: [
        "Detailed written appraisals",
        "Market value assessment",
        "Gemstone identification and grading",
        "Authentication of designer pieces",
        "Insurance documentation",
      ],
      image: "/images/bg-pic.jpg",
    },
    {
      id: 6,
      icon: <FaSearch />,
      title: "Cleaning & Maintenance",
      shortDescription:
        "Professional cleaning and maintenance for your valuable pieces.",
      description:
        "Keep your jewelry and grillz looking their best with our professional cleaning and maintenance services. Our careful attention to detail ensures your pieces maintain their shine and integrity for years to come.",
      features: [
        "Ultrasonic cleaning",
        "Steam cleaning",
        "Polishing and buffing",
        "Prong and setting inspection",
        "Preventative maintenance",
      ],
      image: "/images/img2.jpg",
    },
  ];

  return (
    <ServicesContainer>
      <PageHeader>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          Our Services
        </motion.h1>
        <motion.div
          className="underline"
          initial={{ width: 0 }}
          animate={{ width: "80px" }}
          transition={{ duration: 0.8, delay: 0.2 }}
        />
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          Explore our premium services, each delivered with exceptional
          craftsmanship and attention to detail.
        </motion.p>
      </PageHeader>

      <ServicesGrid
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
        variants={staggerContainer}
      >
        {services.map((service) => (
          <ServiceCard key={service.id} variants={fadeIn}>
            <ServiceCardInner>
              <ServiceFront>
                <IconBox>{service.icon}</IconBox>
                <h3>{service.title}</h3>
                <p>{service.shortDescription}</p>
                <FlipButton>Learn More</FlipButton>
              </ServiceFront>
              <ServiceBack>
                <BackContent>
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  <FeaturesList>
                    {service.features.map((feature, index) => (
                      <FeatureItem key={index}>
                        <FeatureDot />
                        {feature}
                      </FeatureItem>
                    ))}
                  </FeaturesList>
                  <BackButtons>
                    <FlipButton className="back">Go Back</FlipButton>
                    <InquireButton to="/contact">Inquire Now</InquireButton>
                  </BackButtons>
                </BackContent>
              </ServiceBack>
            </ServiceCardInner>
          </ServiceCard>
        ))}
      </ServicesGrid>

      <ProcessSection>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={fadeIn}
        >
          <SectionTitle>Our Process</SectionTitle>
          <ProcessDescription>
            We follow a simple yet comprehensive process to ensure your complete
            satisfaction.
          </ProcessDescription>
        </motion.div>

        <ProcessSteps>
          <ProcessStep>
            <motion.div
              className="step-number"
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              01
            </motion.div>
            <motion.div
              className="step-content"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h3>Initial Consultation</h3>
              <p>
                We begin with a detailed consultation to understand your vision,
                preferences, and requirements for your custom piece.
              </p>
            </motion.div>
          </ProcessStep>

          <ProcessStep>
            <motion.div
              className="step-number"
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              02
            </motion.div>
            <motion.div
              className="step-content"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h3>Design & Approval</h3>
              <p>
                Our designers create detailed sketches or 3D renderings of your
                piece, which you'll review and approve before production begins.
              </p>
            </motion.div>
          </ProcessStep>

          <ProcessStep>
            <motion.div
              className="step-number"
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              03
            </motion.div>
            <motion.div
              className="step-content"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h3>Crafting</h3>
              <p>
                Our skilled artisans carefully craft your piece using premium
                materials and expert techniques, ensuring exceptional quality
                and attention to detail.
              </p>
            </motion.div>
          </ProcessStep>

          <ProcessStep>
            <motion.div
              className="step-number"
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              04
            </motion.div>
            <motion.div
              className="step-content"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h3>Quality Assurance</h3>
              <p>
                Each piece undergoes rigorous quality checks to ensure it meets
                our high standards for craftsmanship, durability, and aesthetic
                excellence.
              </p>
            </motion.div>
          </ProcessStep>

          <ProcessStep>
            <motion.div
              className="step-number"
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              05
            </motion.div>
            <motion.div
              className="step-content"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h3>Delivery & Follow-Up</h3>
              <p>
                We deliver your finished piece with care, provide maintenance
                guidance, and follow up to ensure your complete satisfaction
                with our work.
              </p>
            </motion.div>
          </ProcessStep>
        </ProcessSteps>
      </ProcessSection>

      <CtaSection>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <CtaContent>
            <h2>Ready to Create Something Exceptional?</h2>
            <p>
              Contact us today to discuss your vision and start the journey
              toward your custom piece.
            </p>
            <CtaButton to="/contact">Schedule a Consultation</CtaButton>
          </CtaContent>
        </motion.div>
      </CtaSection>
    </ServicesContainer>
  );
};

// Styled Components
const ServicesContainer = styled.div`
  background-color: var(--dark-bg);
  color: var(--white);
`;

const PageHeader = styled.div`
  padding: 150px 0 80px;
  text-align: center;
  background: linear-gradient(to bottom, var(--darker-bg), var(--dark-bg));
  position: relative;

  h1 {
    font-size: 4rem;
    text-transform: uppercase;
    font-weight: 800;
    background: var(--gold-gradient);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: 20px;

    @media (max-width: 768px) {
      font-size: 3rem;
    }

    @media (max-width: 576px) {
      font-size: 2.5rem;
    }
  }

  .underline {
    height: 4px;
    background: var(--gold-gradient);
    margin: 0 auto;
    border-radius: 2px;
  }

  p {
    max-width: 800px;
    margin: var(--spacing-md) auto 0;
    font-size: 1.1rem;
    color: var(--silver);

    @media (max-width: 768px) {
      font-size: 1rem;
      padding: 0 var(--spacing-md);
    }
  }
`;

// Keyframes
const cardGlow = keyframes`
  0%, 100% {
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  }
  50% {
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4), 0 0 40px rgba(255, 215, 0, 0.1);
  }
`;

const ServicesGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: var(--spacing-lg);
  max-width: 1200px;
  margin: 0 auto;
  padding: var(--spacing-xl) var(--spacing-md);

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled(motion.div)`
  height: 500px;
  perspective: 1500px;

  @media (max-width: 768px) {
    height: 450px;
  }
`;

const ServiceCardInner = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  transition: transform 0.8s;
  transform-style: preserve-3d;

  ${ServiceCard}:hover & {
    transform: rotateY(180deg);
  }
`;

const CardSide = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  padding: var(--spacing-lg);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  border: 1px solid rgba(255, 215, 0, 0.1);
  backdrop-filter: blur(10px);
  transition: all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  ${ServiceCard}:hover & {
    animation: ${cardGlow} 2s ease-in-out infinite;
  }
`;

const ServiceFront = styled(CardSide)`
  background: rgba(255, 255, 255, 0.02);

  h3 {
    font-size: 1.5rem;
    margin: var(--spacing-md) 0;
    color: white;
    transition: color 0.3s ease;
  }

  ${ServiceCard}:hover & h3 {
    color: var(--gold);
  }

  p {
    color: #d0d0d0;
    margin-bottom: var(--spacing-md);
    flex-grow: 1;
  }
`;

const ServiceBack = styled(CardSide)`
  background: rgba(0, 0, 0, 0.85);
  transform: rotateY(180deg);
  padding: var(--spacing-md);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  text-align: left;
  border-color: rgba(255, 215, 0, 0.2);
`;

const BackContent = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;

  h3 {
    font-size: 1.5rem;
    margin-bottom: var(--spacing-sm);
    color: var(--gold);
    position: relative;

    &::after {
      content: "";
      position: absolute;
      bottom: -8px;
      left: 0;
      width: 40px;
      height: 2px;
      background: var(--gold-gradient);
    }
  }

  p {
    color: #d0d0d0;
    margin-bottom: var(--spacing-md);
    font-size: 0.95rem;
    line-height: 1.6;
  }
`;

const FeaturesList = styled.ul`
  margin-bottom: var(--spacing-md);
  flex-grow: 1;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  font-size: 0.9rem;
  color: var(--silver);
  margin-bottom: 8px;
`;

const FeatureDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--gold);
  margin-right: 8px;
  margin-top: 6px;
  flex-shrink: 0;
`;

const BackButtons = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;

  @media (max-width: 576px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const IconBox = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: var(--gold-gradient);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--spacing-md);

  svg {
    font-size: 2.5rem;
    color: var(--black);
  }
`;



const FlipButton = styled.button`
  padding: 12px 25px;
  background: transparent;
  color: var(--gold);
  border: 1px solid var(--gold);
  border-radius: 30px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  transition: var(--transition-fast);

  &:hover {
    background: var(--gold-gradient);
    color: var(--black);
    border-color: transparent;
  }

  &.back {
    padding: 8px 15px;
    font-size: 0.8rem;
  }
`;

const InquireButton = styled(Link)`
  padding: 8px 15px;
  background: var(--gold-gradient);
  color: var(--black);
  border: none;
  border-radius: 30px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
  text-decoration: none;
  text-align: center;
  transition: var(--transition-fast);

  &:hover {
    opacity: 0.9;
    transform: translateY(-3px);
  }
`;

const ProcessSection = styled.section`
  padding: var(--spacing-xl) 0;
  background-color: rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    padding: var(--spacing-lg) 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  color: white;
  text-align: center;
  margin-bottom: var(--spacing-sm);
  position: relative;
  display: inline-block;
  text-transform: uppercase;
  font-weight: 700;
  background: var(--gold-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  display: block;

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  @media (max-width: 576px) {
    font-size: 1.8rem;
  }
`;

const ProcessDescription = styled.p`
  text-align: center;
  font-size: 1.1rem;
  color: var(--silver);
  margin-bottom: var(--spacing-xl);
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: var(--spacing-lg);
    padding: 0 var(--spacing-md);
  }
`;

const ProcessSteps = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 var(--spacing-md);
`;

const ProcessStep = styled.div`
  display: flex;
  margin-bottom: var(--spacing-lg);

  &:last-child {
    margin-bottom: 0;
  }

  .step-number {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background: var(--gold-gradient);
    color: var(--black);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.3rem;
    font-weight: 700;
    flex-shrink: 0;
    margin-right: var(--spacing-md);
    box-shadow: 0 5px 15px rgba(var(--gold-rgb), 0.3);
  }

  .step-content {
    h3 {
      font-size: 1.3rem;
      margin-bottom: 10px;
      color: white;
    }

    p {
      color: #d0d0d0;
      line-height: 1.6;
    }
  }

  @media (max-width: 576px) {
    flex-direction: column;

    .step-number {
      margin-bottom: var(--spacing-sm);
    }
  }
`;

const CtaSection = styled.section`
  padding: var(--spacing-xl) 0;
  background: linear-gradient(to right, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)),
    url("/images/bg-pic.jpg");
  background-size: cover;
  background-position: center;
  background-attachment: fixed;

  @media (max-width: 768px) {
    padding: var(--spacing-lg) 0;
  }
`;

const CtaContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
  text-align: center;
  padding: 0 var(--spacing-md);

  h2 {
    font-size: 2.5rem;
    color: white;
    margin-bottom: var(--spacing-md);

    @media (max-width: 768px) {
      font-size: 2rem;
    }

    @media (max-width: 576px) {
      font-size: 1.8rem;
    }
  }

  p {
    font-size: 1.2rem;
    color: var(--silver);
    margin-bottom: var(--spacing-lg);
    max-width: 700px;
    margin-left: auto;
    margin-right: auto;

    @media (max-width: 768px) {
      font-size: 1.1rem;
    }

    @media (max-width: 576px) {
      font-size: 1rem;
    }
  }
`;

const CtaButton = styled(Link)`
  display: inline-block;
  padding: 15px 40px;
  background: var(--gold-gradient);
  color: var(--black);
  border: none;
  border-radius: 30px;
  font-size: 1.1rem;
  font-weight: 600;
  text-decoration: none;
  transition: var(--transition-medium);

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(var(--gold-rgb), 0.3);
  }

  @media (max-width: 576px) {
    padding: 12px 30px;
    font-size: 1rem;
  }
`;

export default Services;
